import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WalterLoader from '../../../../shared/WalterLoader';
import Stepper from '../../../../shared/Stepper.js';
import { SOURCE_GROUPS, TARGET_GROUPS, detectGreywaterItems } from './calculations/greywaterAuditImport.js';
import { AQUAPOD_MODELS, DEFAULT_NETWORK_WATER_PRICE } from './calculations/aquapodCalculations.js';
import { sumEnabledVolume, evaluateModels, recommendModel, computeValorizedVolume } from './calculations/balanceCalculations.js';
import { computeCategoryBreakdown } from './calculations/categoryBreakdown.js';
import { computeOpexComparison } from './calculations/reutOpexCalculations.js';
import { computeCapex } from './calculations/reutCapexCalculations.js';
import { computePayback } from './calculations/reutRoiCalculations.js';
import SourcesStep from './steps/SourcesStep.js';
import UsagesStep from './steps/UsagesStep.js';
import BalanceStep from './steps/BalanceStep.js';
import BudgetStep from './steps/BudgetStep.js';
import RoiStep from './steps/RoiStep.js';
import '../../actionPlanV2.css';
import './reut.css';

const STEP_LABELS = [
  { label: 'Gisement' },
  { label: 'Débouchés' },
  { label: 'Bilan' },
  { label: 'Budget' },
  { label: 'ROI' },
];

/**
 * Module "Réutilisation des eaux grises (REUT)" — wizard 5 étapes, calqué sur
 * RainwaterHarvestingModule.js : entièrement client-side et éphémère (pas de sauvegarde
 * backend), dérive ses volumes des équipements/usages réels de l'audit.
 */
const ReutModule = () => {
  const navigate = useNavigate();
  const { client_id, building_id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [equipments, setEquipments] = useState([]);
  const [usages, setUsages] = useState([]);
  const [sources, setSources] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [networkPrice, setNetworkPrice] = useState(DEFAULT_NETWORK_WATER_PRICE);

  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStepReached, setFurthestStepReached] = useState(0);

  /* ── Bâtiment (prix de l'eau) + équipements/usages réels de l'audit, pour pré-remplir le
     gisement (douches, lave-linge) et les débouchés (chasses d'eau, ménage, arrosage) ── */
  React.useEffect(() => {
    if (!building_id) return;
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/get_building/${building_id}`).then(r => {
        if (!r.ok) throw new Error("Erreur lors de la récupération du bâtiment.");
        return r.json();
      }),
      fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`).then(r => {
        if (!r.ok) throw new Error("Erreur lors de la récupération des équipements.");
        return r.json();
      }),
    ])
      .then(([buildingData, equipmentsData]) => {
        if (buildingData?.global_water_data?.water_price > 0) {
          setNetworkPrice(buildingData.global_water_data.water_price);
        }
        return fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_equipments/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(equipmentsData),
        })
          .then(r => { if (!r.ok) throw new Error("Erreur lors de la récupération des usages."); return r.json(); })
          .then(usagesData => {
            setEquipments(equipmentsData);
            setUsages(usagesData);
            setSources(detectGreywaterItems(equipmentsData, usagesData, SOURCE_GROUPS));
            setTargets(detectGreywaterItems(equipmentsData, usagesData, TARGET_GROUPS));
          });
      })
      .then(() => setLoading(false))
      .catch(err => { setError(err.message); setLoading(false); });
  }, [building_id]);

  const gisementVolume = useMemo(() => sumEnabledVolume(sources), [sources]);
  const besoinVolume = useMemo(() => sumEnabledVolume(targets), [targets]);

  const categoryBreakdown = useMemo(
    () => computeCategoryBreakdown(equipments, usages, sources, targets),
    [equipments, usages, sources, targets]
  );

  const evaluations = useMemo(
    () => evaluateModels(AQUAPOD_MODELS, gisementVolume, besoinVolume),
    [gisementVolume, besoinVolume]
  );
  const recommendedModelId = useMemo(() => recommendModel(evaluations).model.id, [evaluations]);
  const selectedModel = AQUAPOD_MODELS.find(m => m.id === selectedModelId) || null;

  const valorizedVolume = useMemo(
    () => (selectedModel ? computeValorizedVolume(gisementVolume, besoinVolume, selectedModel) : 0),
    [selectedModel, gisementVolume, besoinVolume]
  );

  const nbConnectedPoints = sources.filter(s => s.enabled).length + targets.filter(t => t.enabled).length;

  const opex = useMemo(() => computeOpexComparison(valorizedVolume, networkPrice), [valorizedVolume, networkPrice]);
  const capex = useMemo(() => computeCapex(selectedModel, nbConnectedPoints), [selectedModel, nbConnectedPoints]);
  const payback = useMemo(() => computePayback(opex.annualSavings, capex.total), [opex.annualSavings, capex.total]);

  const canAdvance = [
    true, // step 0: gisement — toujours avançable
    true, // step 1: débouchés — toujours avançable
    selectedModelId != null, // step 2: bilan — il faut avoir choisi un modèle pour avancer au budget
    true, // step 3: budget — toujours avançable vers le ROI
    false, // step 4: terminal
  ][currentStep];

  const goToStep = (index) => { if (index <= furthestStepReached) setCurrentStep(index); };

  const goNext = () => {
    if (currentStep === STEP_LABELS.length - 1) return;
    const next = currentStep + 1;
    setCurrentStep(next);
    setFurthestStepReached(f => Math.max(f, next));
  };

  const goPrevious = () => { if (currentStep > 0) setCurrentStep(s => s - 1); };

  const selectModel = (modelId) => {
    setSelectedModelId(modelId);
    goNext();
  };

  if (loading) return (
    <div className="centered-spinner-dashboard">
      <WalterLoader />
    </div>
  );
  if (error) return <p>Erreur : {error}</p>;

  const stepProps = { currentStep, totalSteps: STEP_LABELS.length, goPrevious, goNext, canAdvance };

  return (
    <div className="apv2-page">
      <div className="apv2-module-header">
        <button type="button" className="back-button" onClick={() => navigate(`/client/${client_id}/${building_id}/action_plan_v2`)}>
          ← Modules de chiffrage
        </button>
      </div>

      <div className="apv2-wizard-shell">
        <h1 className="apv2-wizard-title">Réutilisation des eaux grises (REUT)</h1>
        <div className="apv2-wizard-nav">
          <button type="button" className="apv2-wizard-nav-btn" onClick={goPrevious} disabled={currentStep === 0}>
            ← Précédent
          </button>
          <Stepper steps={STEP_LABELS} currentStep={currentStep} furthestStepReached={furthestStepReached} onStepClick={goToStep} />
          <button type="button" className="apv2-wizard-nav-btn" onClick={goNext} disabled={!canAdvance || currentStep === STEP_LABELS.length - 1}>
            Suivant →
          </button>
        </div>
      </div>

      {currentStep === 0 && <SourcesStep sources={sources} setSources={setSources} groups={SOURCE_GROUPS} {...stepProps} />}
      {currentStep === 1 && <UsagesStep targets={targets} setTargets={setTargets} groups={TARGET_GROUPS} {...stepProps} />}
      {currentStep === 2 && (
        <BalanceStep
          categoryBreakdown={categoryBreakdown}
          gisementVolume={gisementVolume} besoinVolume={besoinVolume}
          evaluations={evaluations} recommendedModelId={recommendedModelId} networkPrice={networkPrice}
          selectedModelId={selectedModelId} onSelectModel={selectModel}
          {...stepProps}
        />
      )}
      {currentStep === 3 && (
        <BudgetStep
          valorizedVolume={valorizedVolume} selectedModel={selectedModel}
          networkPrice={networkPrice} setNetworkPrice={setNetworkPrice}
          opex={opex} capex={capex} nbConnectedPoints={nbConnectedPoints}
          {...stepProps}
        />
      )}
      {currentStep === 4 && (
        <RoiStep
          valorizedVolume={valorizedVolume} selectedModel={selectedModel}
          opex={opex} capex={capex} payback={payback}
          {...stepProps}
        />
      )}
    </div>
  );
};

export default ReutModule;
